/* eslint-disable react/react-in-jsx-scope */
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { ProgressBar } from "primereact/progressbar";
import { SplitButton } from "primereact/splitbutton";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import moment from "moment";
import { InputText } from "primereact/inputtext";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubscriptionsService } from "../../_shared/services/SubscriptionsService";
import {
  formatPhone,
} from "../../_shared/services/UtilsService";
import {
  Scenario,
  ScenarioStatus,
  Subscription,
} from "../../_shared/types/types";
import ScenarioUpdateModal from "../components/ScenarioUpdateModal";
import { getAdminUserFromLocalStorage } from "../../utils/userLocalStorage";
import { Paginator } from "primereact/paginator";
import config from "../../config.dev.json";
interface ScenariosScreenProps {
  subscription: Subscription;
  onSubscriptionUpdate: (_subscription: Subscription) => void;
}

export default function ScenariosScreen(props: ScenariosScreenProps) {
  const [currentScenarioId, setCurrentScenarioId] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(true);
  const [selectedScenarioType, setSelectedScenarioType] =
    useState<string>("GoogleMaps"); // Estado para o SelectButton

  const [currentSubscription, setCurrentSubscription] = useState<Subscription>(
    props.subscription
  );

  const [first, setFirst] = useState(1);
  const [rows, setRows] = useState(10);

  // Search state
  const [search, setSearch] = useState<string>("");
  const [tempSearch, setTempSearch] = useState<string>("");

  interface PageChangeEvent {
    first: number;
    rows: number;
    page: number;
  }

  const onPageChange = (event: PageChangeEvent) => {
    setCurrentSubscription({
      ...currentSubscription,
      scenarios: [],
    });
    setProcessing(true);
    loadSubscriptionData({
      subscriptionId: currentSubscription.id,
      currentPage: event.page + 1,
      perPage: event.rows,
      updateUrl: true,
      updateParent: false,
      search: search,
    });
    setFirst(event.first);
    setRows(event.rows);
  };

  const toast = useRef<Toast>(null);

  interface LoadSubscriptionDataOptions {
    subscriptionId?: string;
    currentPage?: number;
    perPage?: number;
    updateUrl?: boolean;
    updateParent?: boolean;
    search?: string;
  }

  const loadSubscriptionData = async (options: LoadSubscriptionDataOptions = {}) => {
    // Prevent multiple concurrent calls
    if (processing) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const {
      subscriptionId = props.subscription.id,
      currentPage = Number(urlParams.get("currentPage") || 1),
      perPage = Number(urlParams.get("perPage") || 10),
      updateUrl = false,
      updateParent = true,
      search: paramSearch = urlParams.get("search") || search,
    } = options;

    try {
      const _subscription = await new SubscriptionsService().getSubscriptionByCodeHome(
        subscriptionId,
        currentPage,
        perPage,
        paramSearch || undefined
      );

      if (updateUrl) {
        const urlSearchParams = new URLSearchParams();
        urlSearchParams.set('currentPage', currentPage.toString());
        urlSearchParams.set('perPage', perPage.toString());
        if (paramSearch) urlSearchParams.set('search', paramSearch);
        
        window.history.replaceState(
          {},
          "",
          `home?${urlSearchParams.toString()}`
        );
        setFirst((currentPage - 1) * perPage);
        setRows(perPage);
      }

      setCurrentSubscription(_subscription);
      
      if (updateParent) {
        await props.onSubscriptionUpdate(_subscription);
      }
      
      setProcessing(false);
    } catch (error) {
      setProcessing(false);
      throw error;
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const currentPage = urlParams.get("currentPage") || 1;
    const perPage = urlParams.get("perPage") || 10;
    const urlSearch = urlParams.get("search") || "";
    
    // Initialize search state from URL
    setSearch(urlSearch);
    setTempSearch(urlSearch);
    
    // update url with current page and per page
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set('currentPage', currentPage.toString());
    urlSearchParams.set('perPage', perPage.toString());
    if (urlSearch) urlSearchParams.set('search', urlSearch);
    
    window.history.replaceState(
      {},
      "",
      `?${urlSearchParams.toString()}`
    );

    // Initialize pagination state
    setFirst((Number(currentPage) - 1) * Number(perPage));
    setRows(Number(perPage));
    
    // Start with processing if we don't have complete data yet
    if (!props.subscription.scenarios && !props.subscription.totalScenarios) {
      setProcessing(true);
    }
  }, []);

  // Watch for changes in subscription props (when App.tsx finishes loading)
  useEffect(() => {
    // Check if subscription has scenarios data (indicating App.tsx call is complete)
    if (props.subscription.scenarios !== undefined || props.subscription.totalScenarios !== undefined) {
      setCurrentSubscription(props.subscription);
      setProcessing(false); // Data is loaded, stop processing
    } else {
      setProcessing(true); // Still waiting for data
    }
  }, [props.subscription]);

  const removeSetTimeout = () => {
    const noop = () => { /* intentionally empty */ };
    let id = window.setTimeout(noop, 0);
    while (id--) {
      window.clearTimeout(id);
    }

    // clear all intervals
    id = window.setInterval(noop, 0);
    while (id--) {
      window.clearInterval(id);
    }
  };

  const pause = async (scenarioId: string) => {
    confirmDialog({
      message: "Deseja realmente desativar o cenário?",
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: async () => {
        try {
          setProcessing(true);
          await new SubscriptionsService().pauseScenario(
            props.subscription.id,
            scenarioId
          );
          await loadSubscriptionData();
          setProcessing(false);
          toast?.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Cenário desativado com sucesso",
          });
        } catch (error: unknown) {
          setProcessing(false);
          toast?.current?.show({
            severity: "error",
            summary: "Ooops...",
            detail: error instanceof Error ? error.message : "An unknown error occurred",
          });
        }
      },
    });
  };

  const unpause = async (scenarioId: string) => {
    confirmDialog({
      message: "Deseja realmente re-ativar o cenário?",
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: async () => {
        try {
          setProcessing(true);
          await new SubscriptionsService().unpauseScenario(
            props.subscription.id,
            scenarioId
          );
          await loadSubscriptionData();
          setProcessing(false);
          toast?.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Cenário re-ativado com sucesso",
          });
        } catch (error: unknown) {
          setProcessing(false);
          toast?.current?.show({
            severity: "error",
            summary: "Ooops...",
            detail: error instanceof Error ? error.message : "An unknown error occurred",
          });
        }
      },
    });
  };

  const run = async (scenarioId: string) => {
    confirmDialog({
      message: "Deseja realmente executar o cenário agora?",
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: async () => {
        try {
          setProcessing(true);
          await new SubscriptionsService().executeScenario(
            props.subscription.id,
            scenarioId
          );
          await loadSubscriptionData();
          setProcessing(false);
          toast?.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Execução do cenário iniciada com sucesso",
          });
        } catch (error: unknown) {
          setProcessing(false);
          toast?.current?.show({
            severity: "error",
            summary: "Ooops...",
            detail: error instanceof Error ? error.message : "An unknown error occurred",
          });
        }
      },
    });
  };

  const disconnectScenario = async (scenarioId: string, phone: string) => {
    confirmDialog({
      message: `Deseja realmente desconnectar o número ${formatPhone(phone)} ?`,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: async () => {
        try {
          setProcessing(true);
          await new SubscriptionsService().disconnectScenarioInstance(
            props.subscription.id,
            scenarioId
          );
          await loadSubscriptionData();
          setProcessing(false);
          toast?.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail: "Número desconectado com sucesso",
          });
        } catch (error: unknown) {
          setProcessing(false);
          toast?.current?.show({
            severity: "error",
            summary: "Ooops...",
            detail: error instanceof Error ? error.message : "An unknown error occurred",
          });
        }
      },
    });
  };

  const getNextExecution = async (scenarioId: string) => {
    // Throttling: verificar se já está carregando
    if (nextExecutionLoading[scenarioId]) {
      return;
    }

    // Throttling: verificar se já temos dados recentes (menos de 1 segundo)
    const lastFetch = localStorage.getItem(`nextExecution_${scenarioId}_timestamp`);
    const cachedData = localStorage.getItem(`nextExecution_${scenarioId}_data`);
    
    if (lastFetch && (Date.now() - parseInt(lastFetch)) < 1000) {
      // Mostrar dados em cache se disponível
      if (cachedData) {
        try {
          const data = JSON.parse(cachedData);
          if (data && data !== null && data !== 'null' && data !== '') {
            const formattedDate = moment(data).format('DD/MM/YYYY HH:mm');
            toast?.current?.show({
              severity: "info",
              summary: "Próxima Execução (Cache)",
              detail: `Próxima execução: ${formattedDate}`,
              life: 1000,
            });
          } else {
            toast?.current?.show({
              severity: "info",
              summary: "Próxima Execução (Cache)",
              detail: "Nenhuma execução agendada para este cenário",
              life: 1000,
            });
          }
        } catch (error) {
          console.error('Erro ao processar dados em cache:', error);
        }
      }
      return;
    }

    try {
      setNextExecutionLoading(prev => ({ ...prev, [scenarioId]: true }));
      
      const response = await fetch(
        `${config.BASE_URL}/subscriptions/${props.subscription.id}/scenarios/${scenarioId}/next-execution`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const nextExecution = data.nextExecution;

      // Armazenar dados e timestamp
      setNextExecutionData(prev => ({ ...prev, [scenarioId]: nextExecution }));
      localStorage.setItem(`nextExecution_${scenarioId}_timestamp`, Date.now().toString());
      localStorage.setItem(`nextExecution_${scenarioId}_data`, JSON.stringify(nextExecution));
      
      // Mostrar toast com a data formatada ou mensagem de não agendado
      if (nextExecution && nextExecution !== null) {
        try {
          const formattedDate = moment(nextExecution).format('DD/MM/YYYY HH:mm');
          toast?.current?.show({
            severity: "info",
            summary: "Próxima Execução",
            detail: `Próxima execução: ${formattedDate}`,
            life: 3000,
          });
        } catch (dateError) {
          console.error('Erro ao formatar data:', dateError);
          toast?.current?.show({
            severity: "info",
            summary: "Próxima Execução",
            detail: `Próxima execução: ${nextExecution}`,
            life: 3000,
          });
        }
      } else {
        toast?.current?.show({
          severity: "info",
          summary: "Próxima Execução",
          detail: "Nenhuma execução agendada para este cenário",
          life: 3000,
        });
      }
      
    } catch (error: unknown) {
      toast?.current?.show({
        severity: "error",
        summary: "Ooops...",
        detail: error instanceof Error ? error.message : "Erro ao buscar próxima execução",
      });
    } finally {
      setNextExecutionLoading(prev => ({ ...prev, [scenarioId]: false }));
    }
  };

  const [showQRCode, setShowQRCode] = useState<boolean>(false);
  const [qrCodeInfo, setQrCodeInfo] = useState<{qrcode?: {value: string}, error?: unknown} | undefined>(undefined);
  const [nextExecutionLoading, setNextExecutionLoading] = useState<{[key: string]: boolean}>({});
  const [nextExecutionData, setNextExecutionData] = useState<{[key: string]: string}>({});

  const reconnect = async (scenarioId: string) => {
    setShowQRCode(true);
    setProcessing(true);
    setCurrentScenarioId(scenarioId);

    return await new SubscriptionsService()
      .getScenarioQRCode(props.subscription.id, scenarioId)
      .then((qrCodeData) => {
        setQrCodeInfo(qrCodeData);
        setShowQRCode(true);
        if (qrCodeData && qrCodeData.qrcode?.value) {
          setTimeout(() => check(scenarioId), 1000);
        } else {
          if (showQRCode) {
            setTimeout(() => reconnect(scenarioId), 5000);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        setQrCodeInfo({ error: error });
      });
  };

  let intervalId: NodeJS.Timeout;
  const check = async (scenarioId: string) => {

    setCurrentScenarioId(scenarioId);
    const checkConnection = async () => {
      const checkData =
        await new SubscriptionsService().checkScenarioInstanceConnection(
          props.subscription.id,
          scenarioId
        );

      if (checkData.connected) {
        ("conectou");
        clearInterval(intervalId);
        removeSetTimeout();

        await loadSubscriptionData();
        setProcessing(false);
        setShowQRCode(false);
        setCurrentScenarioId("");
        setQrCodeInfo(undefined);

        toast?.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Whatsapp re-conectado com sucesso",
        });
      } else {
        ("ainda nao conectou");
      }
      ("checkData");
    };

    intervalId = setInterval(checkConnection, 2000);

    setTimeout(async () => {
      clearInterval(intervalId);

      toast?.current?.show({
        severity: "error",
        summary: "Ooops...",
        detail: "Tempo excedido de conexão. Por favor tente novamente.",
      });

              setQrCodeInfo(undefined);
      setProcessing(false);
      setShowQRCode(false);

      await loadSubscriptionData();
    }, 300000); // 5 minutes
    // Initial check
    await checkConnection();
  };

  const save = async () => {
    if (selectedScenario) {
      try {
        const {
          qtd_leads,
          category,
          lat_long,
          geo_filter,
          other_filter,
          sales_rep_name,
          prompt,
          bot_active,
          webhook,
          scenario_name,
          test_mode,
          test_mode_phone,
          prompt_memory_window,
          prompt_max_messages,
          config,
        } = selectedScenario;
        setProcessing(true);
        await new SubscriptionsService().saveScenario(
          props.subscription.id,
          selectedScenario?.scenario_id,
          {
            qtd_leads,
            category,
            lat_long,
            geo_filter,
            other_filter,
            sales_rep_name,
            bot_active,
            prompt,
            webhook,
            scenario_name,
            test_mode,
            test_mode_phone,
            prompt_memory_window,
            prompt_max_messages,
            config,
          }
        );
        await loadSubscriptionData();
        setProcessing(false);
        setSelectedScenario(undefined);
        setShowParameters(false);
        toast?.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Cenário atualizado com sucesso",
        });
      } catch (error: unknown) {
        setProcessing(false);
        toast?.current?.show({
          severity: "error",
          summary: "Ooops...",
          detail: error instanceof Error ? error.message : "An unknown error occurred",
        });
      }
    }
  };
  const cleanTestMessage = async () => {
    if (selectedScenario) {
      try {
        setProcessing(true);
        await new SubscriptionsService().clearScenarioTestMessages(
          props.subscription.id,
          selectedScenario?.scenario_id
        );
        await loadSubscriptionData();
        setSelectedScenario(
          Object.assign(selectedScenario, { test_messages_count: 0 })
        );
        setProcessing(false);
        toast?.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Mensagens de teste apagadas com sucesso",
        });
      } catch (error: unknown) {
        setProcessing(false);
        toast?.current?.show({
          severity: "error",
          summary: "Ooops...",
          detail: error instanceof Error ? error.message : "An unknown error occurred",
        });
      }
    }
  };

  const [showParameters, setShowParameters] = useState<boolean>(true);
  const [selectedScenario, setSelectedScenario] = useState<
    Scenario | undefined
  >(undefined);

  const navigate = useNavigate();

  const jsonList =
    '{\n  "sheetData": {\n    "F": "",\n    "G": "",\n    "H": "",\n    "I": "",\n    "J": "",\n    "K": "",\n    "L": "",\n    "M": "",\n    "N": "",\n    "O": ""\n  },\n  "sheetName": "",\n  "spreadsheetId": ""\n}';

  const [showCreateScenario, setShowCreateScenario] = useState<boolean>(false);
  const [newScenario, setNewScenario] = useState<Scenario>({
    scenario_name: "",
    customer: "",
    instance_name: "",
    scenario_id: "",
    is_make_active: false,
    status: ScenarioStatus.InstanceDisconnected,
    config: selectedScenarioType === "List" ? jsonList : {},
  });

  const createScenario = async () => {
    confirmDialog({
      message: `Deseja realmente criar o cenário ${newScenario.scenario_name} ?`,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          setProcessing(true);
          await new SubscriptionsService().createScenario(
            props.subscription.id,
            newScenario.scenario_name,
            selectedScenarioType
          );
          setProcessing(false);
          setNewScenario({
            scenario_name: "",
            customer: "",
            instance_name: "",
            scenario_id: "",
            is_make_active: false,
            status: ScenarioStatus.InstanceDisconnected,
          });
          setShowCreateScenario(false);
          await loadSubscriptionData();
          toast?.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail:
              "Criação do cenário iniciada com sucesso. Por favor atualize a página dento de alguns instantes caso o novo cenário não apareça na lista.",
          });
        } catch (error: unknown) {
          setProcessing(false);
          toast?.current?.show({
            severity: "error",
            summary: "Ooops...",
            detail: error instanceof Error ? error.message : "An unknown error occurred",
          });
        }
      },
    });
  };

  const updateScenario = async (scenario: Scenario) => {
    navigate(
      `/admin/${props.subscription.id}/scenarios/${scenario.scenario_id}/edit`
    );
  };

  const navigateToEditScenario = async (scenario: Scenario) => {
    navigate(
      `/${props.subscription.id}/scenarios/${scenario.scenario_id}/edit`
    );
  };

  const deleteScenario = async (scenario: Scenario) => {
    confirmDialog({
      message: `Deseja realmente deletar o cenário ${scenario.scenario_name} ?`,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          setProcessing(true);
          await new SubscriptionsService().deleteScenario(
            props.subscription.id,
            scenario.scenario_id
          );
          setProcessing(false);
          await loadSubscriptionData();
          toast?.current?.show({
            severity: "success",
            summary: "Sucesso",
            detail:
              "Deleção do cenário iniciada com sucesso. Por favor atualize a página dento de alguns instantes caso o cenário não desapareça da lista.",
          });
        } catch (error: unknown) {
          setProcessing(false);
          toast?.current?.show({
            severity: "error",
            summary: "Ooops...",
            detail: error instanceof Error ? error.message : "An unknown error occurred",
          });
        }
      },
    });
  };

  const getScenarioActions = (scenario: Scenario): ReactNode => {
    const items = [];

    items.push({
      label: "Conversas",
      icon: "pi pi-whatsapp",
      command: () => {
        navigate(
          `/${props.subscription.id}/scenarios/${scenario.scenario_id}/zap`
        );
      },
    });

    const isSubscriptionActive = props.subscription.status === "active";
      const isPhoneConnected = scenario.connectedPhone && 
                               scenario.connectedPhone.trim() !== "" &&
                               scenario.connectedPhone.toLowerCase() !== "disconnected" &&
                               scenario.status !== ScenarioStatus.InstanceDisconnected;

      const isActive = scenario.is_make_active === true &&
                       scenario.data_source &&
                       ["list", "googlemaps"].includes(scenario.data_source.toLowerCase());

      // Botão "Executar Agora" - só quando conectado, ativo e com cenário válido
      if (isSubscriptionActive && isPhoneConnected && isActive) {
        items.push({
          label: "Executar Agora",
          icon: "pi pi-bolt",
          command: () => run(scenario.scenario_id),
        });

        const isLoadingNextExecution = nextExecutionLoading[scenario.scenario_id];
        items.push({
          label: isLoadingNextExecution ? "Carregando..." : "Próxima Execução",
          icon: isLoadingNextExecution ? "pi pi-spin pi-spinner" : "pi pi-calendar",
          command: () => getNextExecution(scenario.scenario_id),
          disabled: isLoadingNextExecution,
        });
      }

      // Botão "Conectar" - só quando ativo e desconectado
      if (isSubscriptionActive && !isPhoneConnected) {
        items.push({
          label: "Conectar",
          icon: "pi pi-qrcode",
          command: () => reconnect(scenario.scenario_id),
        });
      }

    items.push({
      label: "Configurar",
      icon: "pi pi-cog",
      command: () => {
        setSelectedScenario(scenario);
        setShowParameters(true);
      },
    });

    if (
      isSubscriptionActive &&
      scenario.is_make_active === false
    ) {
      items.push({
        label: "Ativar",
        icon: "pi pi-play",
        command: () => unpause(scenario.scenario_id),
      });
    }

    if (
      isSubscriptionActive &&
      scenario.is_make_active === true
    ) {
      items.push({
        label: "Desativar",
        icon: "pi pi-pause",
        command: () => pause(scenario.scenario_id),
      });
    }

    if (
      isSubscriptionActive &&
      scenario.status !== ScenarioStatus.InstanceDisconnected
      && scenario.connectedPhone 
      && scenario.connectedPhone.toLowerCase() !== "disconnected"
    ) {
      items.push({
        label: "Desconectar",
        icon: "pi pi-power-off",
        command: () => {
          disconnectScenario(
            scenario.scenario_id,
            scenario.connectedPhone ?? ""
          );
        },
      });
    }

    if (getAdminUserFromLocalStorage()?.email || currentSubscription.isEdit) {
      items.push({
        label: "Editar",
        icon: "pi pi-pencil",
        command: () =>
          currentSubscription.isEdit
            ? navigateToEditScenario(scenario)
            : updateScenario(scenario),
      });
    }

    items.push({
      label: "Deletar",
      icon: "pi pi-trash",
      command: () => deleteScenario(scenario),
    });

    return (
      <span className="p-buttonset">
        {items.splice(0, 2).map((item, index) => (
          <Button
            key={index}
            label={item.label}
            icon={item.icon}
            onClick={item.command}
            size="small"
            className="p-button-text p-button-plain"
          />
        ))}
        {items.length >= 2 ? (
          <SplitButton
            disabled={processing}
            label={items[0].label}
            icon={items[0].icon}
            dropdownIcon="pi pi-ellipsis-v"
            onClick={items[0].command}
            model={items.splice(1, items.length)}
            size="small"
            className="p-button-text p-button-plain"
            style={{ color: '#5371ff' }}
          />
        ) : (
          <SplitButton
            disabled={processing}
            label={items[0].label}
            icon={items[0].icon}
            onClick={items[0].command}
            model={items.splice(0, items.length)}
            size="small"
            className="p-button-text p-button-plain"
            style={{ color: '#5371ff' }}
          />
        )}
      </span>
    );
  };

  const executeSearch = async () => {
    setSearch(tempSearch);
    setCurrentSubscription({
      ...currentSubscription,
      scenarios: [],
    });
    setProcessing(true);
    
    await loadSubscriptionData({
      subscriptionId: currentSubscription.id,
      currentPage: 1, // Reset to first page on search
      perPage: rows,
      updateUrl: true,
      updateParent: false,
      search: tempSearch,
    });
    
    setFirst(0); // Reset pagination to first page
  };

  const clearSearch = async () => {
    setSearch("");
    setTempSearch("");
    setCurrentSubscription({
      ...currentSubscription,
      scenarios: [],
    });
    setProcessing(true);
    
    await loadSubscriptionData({
      subscriptionId: currentSubscription.id,
      currentPage: 1,
      perPage: rows,
      updateUrl: true,
      updateParent: false,
      search: "",
    });
    
    setFirst(0); // Reset pagination to first page
  };

  const onCloseQrCode = async () => {
    removeSetTimeout();

    if (
      currentScenarioId !== "" &&
      currentScenarioId.length &&
      showQRCode &&
      qrCodeInfo &&
      qrCodeInfo.qrcode?.value
    ) {
      await new SubscriptionsService()
        .disconnectScenarioInstance(props.subscription.id, currentScenarioId)
        .then(() => {
          setCurrentScenarioId("");
        })
        .catch((error) => {
          console.log("error", error);
        });
    }

    setShowQRCode(false);
    setProcessing(false);
            setQrCodeInfo(undefined);

    await loadSubscriptionData();
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="p-4 w-full">
        <div className="flex justify-content-between align-items-center mb-5 w-full">
          <div className="text-xl text-900 font-medium">
            Créditos e Cenários
            {currentSubscription.status === "inactive" && (
              <Tag
                severity="warning"
                value="Subscrição  Inativa"
                className="ml-4"
              ></Tag>
            )}
          </div>
          <div className="surface-card shadow-2 border-round text-lg text-900 font-small p-2 flex align-items-center" style={{ backgroundColor: '#f8f9ff', border: '1px solid #e6e9ff' }}>
            <Button
              label="Ver Créditos, Consumo e Saldo"
              link
              style={{ color: '#5371ff', fontWeight: '600' }}
              onClick={() =>
                navigate(`/${currentSubscription.id}/statement`)
              }
            />
          </div>
          {currentSubscription.creditsInfo && (
            <div className="surface-card shadow-2 border-round p-3 flex align-items-center ml-2" style={{ 
              backgroundColor: 'linear-gradient(135deg, #f8f9ff 0%, #e6e9ff 100%)', 
              border: '1px solid #d1d5ff',
              minWidth: '400px'
            }}>
              <div className="flex align-items-center justify-content-between w-full">
                <div className="flex align-items-center mr-4">
                  <i className="pi pi-credit-card mr-2" style={{ color: '#5371ff', fontSize: '1.2rem' }}></i>
                  <span className="font-semibold" style={{ color: '#5371ff' }}>Créditos:</span>
                  <span className="ml-1 font-bold" style={{ color: '#2c3e50' }}>
                    {Intl.NumberFormat("pt-BR").format(currentSubscription.creditsInfo.total)}
                  </span>
                </div>
                <div className="flex align-items-center mr-4">
                  <i className="pi pi-chart-line mr-2" style={{ color: '#5371ff', fontSize: '1.2rem' }}></i>
                  <span className="font-semibold" style={{ color: '#5371ff' }}>Consumo:</span>
                  <span className="ml-1 font-bold" style={{ color: '#2c3e50' }}>
                    {Intl.NumberFormat("pt-BR").format(currentSubscription.creditsInfo.spent)}
                  </span>
                </div>
                <div className="flex align-items-center">
                  <i className="pi pi-wallet mr-2" style={{ color: '#5371ff', fontSize: '1.2rem' }}></i>
                  <span className="font-semibold" style={{ color: '#5371ff' }}>Saldo:</span>
                  <span className="ml-1 font-bold" style={{ color: '#2c3e50' }}>
                    {Intl.NumberFormat("pt-BR").format(currentSubscription.creditsInfo.remaining)}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div>
            <Button
              label="Criar"
              icon="pi pi-plus"
              onClick={() => setShowCreateScenario(true)}
              style={{ marginLeft: "10px", backgroundColor: '#5371ff', borderColor: '#5371ff' }}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 align-items-center mb-4">
          <div className="flex align-items-center">
            <label htmlFor="search" className="mr-2 font-medium">Buscar:</label>
            <InputText
              id="search"
              value={tempSearch}
              onChange={(e) => setTempSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone..."
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  executeSearch();
                }
              }}
              style={{ width: "300px" }}
            />
          </div>
          <div className="flex gap-2">
            <Button
              icon="pi pi-search"
              label="Buscar"
              onClick={executeSearch}
              size="small"
              disabled={processing}
              style={{ backgroundColor: '#5371ff', borderColor: '#5371ff' }}
            />
            <Button
              icon="pi pi-times"
              label="Limpar"
              onClick={clearSearch}
              size="small"
              severity="secondary"
              outlined
              disabled={processing}
              style={{ color: '#5371ff', borderColor: '#5371ff' }}
            />
          </div>
        </div>
        {processing && (
          <>
            <div className="w-full mb-2">
              <ProgressBar
                mode="indeterminate"
                style={{ height: "6px" }}
              ></ProgressBar>
            </div>
          </>
        )}
        <DataTable
          value={processing ? [] : (currentSubscription.scenarios || [])}
          tableStyle={{ minWidth: "50rem" }}
          emptyMessage={
            processing ? "Carregando..." : "Nenhum cenário encontrado"
          }
        >
          <Column field="scenario_name" header="Nome"></Column>
                     <Column
             header="Status Cenário"
             body={(scenario: Scenario) => (
               <>
                 {scenario.is_make_active === true ? (
                   <Tag
                     className="text-xs"
                     severity="success"
                     value="Ativado"
                   ></Tag>
                 ) : scenario.is_make_active === false ? (
                   <Tag
                     className="text-xs"
                     severity="warning"
                     value="Desativado"
                   ></Tag>
                 ) : (
                   <></>
                 )}
                 {scenario.test_mode && (
                   <>
                     <br />
                     <Tag
                       className="text-xs mt-2"
                       icon="pi pi-exclamation-triangle"
                       severity="warning"
                       value="Modo Teste"
                     ></Tag>
                   </>
                 )}
               </>
             )}
           ></Column>
          <Column
            header="Status Whatsapp"
            body={(scenario: Scenario) => (
              <>
                {scenario.status === ScenarioStatus.Running && 
                 scenario.connectedPhone &&
                 scenario.connectedPhone.trim().toLowerCase() !== "disconnected" ? (
                  <Tag
                    className="text-xs"
                    severity="success"
                    value="Conectado"
                  ></Tag>
                ) : scenario.status === ScenarioStatus.Paused ? (
                  <Tag
                    className="text-xs"
                    severity="warning"
                    value="Desativado"
                  ></Tag>
                ) : (
                  <Tag
                    className="text-xs"
                    severity="danger"
                    value="Desconectado"
                  ></Tag>
                )}
              </>
            )}
          ></Column>
          <Column
            header="Número Conectado"
            body={(scenario: Scenario) => {
              const shouldBeEmpty = 
                scenario.status === ScenarioStatus.InstanceDisconnected ||
                !scenario.connectedPhone ||
                scenario.connectedPhone.trim().toLowerCase() === "disconnected" ||
                scenario.connectedPhone.trim() === "";
              
              return shouldBeEmpty ? "" : formatPhone(scenario.connectedPhone || "");
            }}
          ></Column>
          <Column
            className="text-right"
            body={(scenario: Scenario) => getScenarioActions(scenario)}
          ></Column>
        </DataTable>
        <div className="card">
          <Paginator
            first={first}
            rows={rows}
            totalRecords={currentSubscription.totalScenarios}
            rowsPerPageOptions={[10]}
            onPageChange={onPageChange}
          />
        </div>

        {/* Link para Changelog - Posicionado após a tabela */}
        <div className="flex justify-content-center mt-4">
          <Button
            label="Novidades v2.0.0"
            icon="pi pi-info-circle"
            size="small"
            className="p-button-info p-button-outlined"
            onClick={() => navigate(`/${currentSubscription.id}/changelog`)}
            tooltip="Ver as últimas atualizações da plataforma"
            tooltipOptions={{ position: 'top' }}
            style={{
              backgroundColor: 'rgba(83, 113, 255, 0.1)',
              borderColor: '#5371ff',
              color: '#5371ff',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              borderRadius: '8px',
            }}
          />
        </div>
      </div>
      <Dialog
        header="QR Code"
        visible={showQRCode}
        onHide={() => onCloseQrCode()}
      >
        {qrCodeInfo && qrCodeInfo.qrcode?.value ? (
          <>
            <img src={qrCodeInfo.qrcode.value} alt="QR Code" />
          </>
        ) : (
          <div
            className="w-full"
            style={{
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
            }}
          >
            {qrCodeInfo && !!qrCodeInfo.error && (
              <p>
                Não há instâncias disponivel <br /> tente novamente ou entre em
                contato com suporte{" "}
              </p>
            )}
            {!qrCodeInfo && (
              <>
                <p>Aguardando Instancia</p>
                <ProgressBar
                  mode="indeterminate"
                  style={{ height: "6px" }}
                  title="Aguardando Instancia"
                />
              </>
            )}
          </div>
        )}
      </Dialog>
      {showParameters && selectedScenario && (
        <>
          <ScenarioUpdateModal
            selectedScenario={selectedScenario}
            onScenarioUpdate={(scenario: Scenario) =>
              setSelectedScenario(scenario)
            }
            onSave={() => save()}
            onHide={() => {
              setShowParameters(false);
              setProcessing(false);
            }}
            processing={processing}
            onCleanTestMessage={() => cleanTestMessage()}
          />
        </>
      )}
      {showCreateScenario && (
        <>
          <Dialog
            header={"Criar Cenário"}
            visible={true}
            onHide={() => {
              setShowCreateScenario(false);
              setProcessing(false);
            }}
            footer={
              <div>
                <Button
                  label="Cancelar"
                  icon="pi pi-times"
                  onClick={() => {
                    setShowCreateScenario(false);
                    setProcessing(false);
                  }}
                  className="p-button-text"
                  style={{ color: '#5371ff' }}
                />
                <Button
                  label="Salvar"
                  icon="pi pi-check"
                  onClick={() => createScenario()}
                  autoFocus
                  style={{ backgroundColor: '#5371ff', borderColor: '#5371ff' }}
                />
              </div>
            }
          >
            <div
              className="w-full"
              style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                minWidth: "400px",
              }}
            >
              <div className="font-medium text-900 flex flex-column mb-2">
                <div className="font-bold flex align-items-center mb-2">
                  Nome:
                </div>
                <div className="w-full mb-2">
                  <InputText
                    value={newScenario.scenario_name ?? ""}
                    onChange={(e) =>
                      setNewScenario(
                        Object.assign(
                          {},
                          { ...newScenario, scenario_name: e.target.value }
                        )
                      )
                    }
                    style={{ width: "100%", maxWidth: "400px" }}
                  />
                </div>
              </div>
            </div>
            <div className="font-medium text-900 flex flex-column mb-2">
              <div className="font-bold flex align-items-center mb-2">
                <label htmlFor="scenarioType" className="font-bold">
                  Tipo:
                </label>
              </div>
              <Dropdown
                id="scenarioType"
                value={selectedScenarioType}
                options={[
                  { label: "GoogleMaps", value: "GoogleMaps" },
                  { label: "List", value: "List" },
                  { label: "CRM", value: "CRM" },
                ]}
                onChange={(e) => setSelectedScenarioType(e.value)}
              />
            </div>
          </Dialog>
        </>
      )}

    </>
  );
}
